<template>
  <v-dialog v-model="localModelValue" max-width="800px" persistent>
    <v-card class="upload-modal-card">
      <div class="modal-content">
        <div class="modal-header-custom">
          <button class="back-button" @click="handleCancel">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="#003d6a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="back-text">Back</span>
          </button>
        </div>

        <input
          ref="fileInputRef"
          type="file"
          @change="handleFileSelect"
          style="display: none"
          accept="application/zip"
        />

        <div
          v-if="!selectedFile"
          class="upload-area"
          :class="{ 'drag-over': isDragOver }"
          @drop="handleDrop"
          @dragover.prevent="handleDragOver"
          @dragenter.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @click="triggerFileInput"
        >
          <div class="upload-button-wrapper">
            <span class="upload-button-text">Choose File</span>
            <svg
              width="43"
              height="43"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="#003d6a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <p class="upload-hint">Or drag and drop them here</p>
        </div>

        <div v-else class="file-preview-container">
          <div class="file-preview-card">
            <div class="file-title-section">
              <h3 class="file-title">{{ selectedFile.name }}</h3>
              <div class="file-divider"></div>
            </div>
            <div class="file-actions">
              <button class="remove-file-link" @click="removeFile">
                Remove File
              </button>
              <button class="change-file-button" @click="triggerFileInput">
                Change File
              </button>
            </div>
          </div>
        </div>

        <div v-if="errors.file" class="error-message">{{ errors.file }}</div>
        <div v-if="errors.title" class="error-message">{{ errors.title }}</div>

        <div class="modal-footer">
          <button
            class="save-button"
            :disabled="!selectedFile || saving"
            @click="handleSave"
          >
            <span v-if="saving">Saving...</span>
            <span v-else>Save Scorm</span>
          </button>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";
import ScormFilesService from "@/services/scormFilesService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "save", "cancel"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const selectedFile = ref(null);
const isDragOver = ref(false);
const fileInputRef = ref(null);
const saving = ref(false);
const errors = ref({
  file: "",
  title: "",
});

const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      errors.value.file = "Please select a ZIP file";
      return;
    }
    selectedFile.value = file;
    isDragOver.value = false;
    errors.value.file = "";
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragOver.value = false;

  const file = event.dataTransfer.files[0];
  if (file) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      errors.value.file = "Please select a ZIP file";
      return;
    }
    selectedFile.value = file;
    errors.value.file = "";
  }
};

const removeFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
  errors.value.file = "";
};

const handleCancel = () => {
  selectedFile.value = null;
  isDragOver.value = false;
  errors.value = { file: "", title: "" };
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
  emit("cancel");
  localModelValue.value = false;
};

const handleSave = async () => {
  if (saving.value || !selectedFile.value) return;

  errors.value = { file: "", title: "" };

  if (!selectedFile.value) {
    errors.value.file = "File is required";
    return;
  }

  saving.value = true;
  try {
    const response = await ScormFilesService.upload(selectedFile.value);
    if (response.status === 200) {
      emit("save", { file: selectedFile.value });
      handleCancel();
    }
  } catch (error) {
    console.error("Error uploading SCORM file:", error);
    errors.value.file = error.message || "Failed to upload SCORM file";
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.upload-modal-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  background-color: white;
}

.modal-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.modal-header-custom {
  display: flex;
  align-items: center;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: "League Spartan", sans-serif;
  color: var(--primary-blue);
  font-size: var(--fs-medium);
  font-weight: 500;
}

.back-button:hover {
  opacity: 0.8;
}

.back-text {
  color: var(--primary-blue);
}

.upload-area {
  border: 1px dashed #0478ce;
  border-radius: 16px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: white;
}

.upload-area:hover {
  background-color: #f8f9fa;
}

.upload-area.drag-over {
  background-color: #e8f4f8;
  border-color: var(--primary-blue);
}

.upload-button-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 48px;
  border-radius: 16px;
}

.upload-button-text {
  font-family: "Inter", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
  white-space: nowrap;
}

.upload-hint {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #98a2b3;
  margin-top: 12px;
  text-align: center;
}

.file-preview-container {
  width: 100%;
}

.file-preview-card {
  border: 1px solid #0478ce;
  border-radius: 16px;
  padding: 24px;
  background-color: white;
}

.file-title-section {
  margin-bottom: 16px;
}

.file-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

.file-divider {
  height: 1px;
  width: 100%;
  background-color: #dfe2e7;
  margin-top: 12px;
}

.file-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.remove-file-link {
  background: none;
  border: none;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #6c737f;
  padding: 0;
}

.remove-file-link:hover {
  color: #e00069;
}

.change-file-button {
  padding: 8px 24px;
  border: 1px solid #0478ce;
  border-radius: 8px;
  background-color: white;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.change-file-button:hover {
  background-color: #f8f9fa;
}

.error-message {
  color: #e00069;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  margin-top: -16px;
}

.modal-footer {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.save-button {
  width: 150px;
  height: 56px;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.save-button:disabled {
  background-color: #dfe2e7 !important;
  color: #cccccc !important;
  cursor: not-allowed;
  opacity: 1;
}

.save-button:not(:disabled) {
  background-color: var(--primary-blue) !important;
  color: white !important;
}

.save-button:not(:disabled):hover {
  background-color: #00599a !important;
}

@media (max-width: 768px) {
  .modal-content {
    padding: 16px;
    gap: 16px;
  }

  .upload-area {
    height: 250px;
    padding: 20px;
  }

  .upload-button-text {
    font-size: var(--fs-big);
  }

  .file-title {
    font-size: var(--fs-big);
  }

  .file-actions {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .change-file-button {
    width: 100%;
  }

  .modal-footer {
    flex-direction: column;
    width: 100%;
  }

  .save-button {
    width: 100%;
  }
}
</style>
